import React, { useState, useEffect } from 'react';
import { Button, Dropdown, Form, Table, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import {
    BiPlus, BiEdit, BiCopy, BiShow, BiTrash, BiLayout, BiLink, BiBarChartAlt2,
    BiSearch, BiDotsHorizontalRounded,
} from 'react-icons/bi';
import { Buffer } from 'buffer';
import Topbar from '../component/Topbar';
import LoadingScreen from '../component/LoadingScreen';
import FormPreviewModal from '../component/FormPreviewModal';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import { TemplatePagination, TemplateListItem, TemplateStatus, TemplateField } from '../types/template';

const PAGE_SIZE = 20;

function StatusBadge({ status }: { status: TemplateStatus }) {
    return (
        <span className={`template-status-badge template-status-badge--${status.toLowerCase()}`}>
            {status}
        </span>
    );
}

export default function Forms() {
    const { instance, accounts, inProgress } = useMsal();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [role, setRole]       = useState<UserRole>('owner');
    const [token, setToken]     = useState('');
    const [photoData, setPhotoData] = useState<string | null>(null);
    const [data, setData]       = useState<TemplatePagination | null>(null);
    const [page, setPage]       = useState(0);
    const [keyword, setKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [tableLoading, setTableLoading] = useState(false);

    // Preview modal
    const [preview, setPreview] = useState<{
        name: string;
        fields: TemplateField[];
        description?: string | null;
        languages?: string[];
        names?: Record<string, string>;
        descriptions?: Record<string, string>;
    } | null>(null);

    const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
    const api  = process.env.REACT_APP_API_END_POINT;

    // ── Auth init ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        acquireGetAccessToken(instance, accounts).then(async (tokens: any) => {
            if (!tokens) return;
            try {
                const r = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${tokens.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                });
                setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r.data, 'binary').toString('base64'));
            } catch { /* no photo */ }
            const userRole = await checkRole(tokens.accessTokenProfile);
            saveRealRole(userRole);
            if (userRole !== 'admin' && userRole !== 'superadmin') {
                navigate('/accessdenied'); return;
            }
            setRole(getEffectiveRole(userRole));
            setToken(tokens.accessToken);
        });
    }, [inProgress, instance, accounts, navigate]);

    // ── Load forms ────────────────────────────────────────────────────────────
    const load = async (p = 0, kw = keyword, st = statusFilter) => {
        if (!token) return;
        setTableLoading(true);
        try {
            const res = await axios.get<TemplatePagination>(
                `${api}/v1/Form/list`,
                { headers: { Authorization: `Bearer ${token}` }, params: { keyword: kw, status: st || undefined, offset: p * PAGE_SIZE, limit: PAGE_SIZE } }
            );
            setData(res.data);
        } catch {
            toast.error('Failed to load forms');
        } finally {
            setTableLoading(false);
            setLoading(false);
        }
    };

    useEffect(() => { if (token) load(0); }, [token]); // eslint-disable-line

    const handleSearch = () => { setPage(0); load(0, keyword, statusFilter); };

    // ── Actions ───────────────────────────────────────────────────────────────
    const handleDuplicate = async (code: string) => {
        try {
            const res = await axios.post(`${api}/v1/Form/duplicate`, null, { ...auth(), params: { code } });
            toast.success(`Duplicated as ${res.data.templateCode}`);
            load(page);
        } catch { toast.error('Failed to duplicate form'); }
    };

    const handleDelete = async (t: TemplateListItem) => {
        const result = await Swal.fire({
            title: 'Delete this form?',
            text:  'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#c5281f',
            confirmButtonText: 'Delete',
        });
        if (!result.isConfirmed) return;
        try {
            await axios.delete(`${api}/v1/Form/delete`, { ...auth(), params: { code: t.templateCode } });
            toast.success('Form deleted');
            load(page);
        } catch { toast.error('Failed to delete form'); }
    };

    const handleCopyUrl = (code: string) => {
        const base = (process.env.REACT_APP_PUBLIC_URL ?? '').replace(/\/$/, '');
        const url  = `${window.location.origin}${base}/form/${code}`;
        navigator.clipboard.writeText(url).then(() => toast.success('Form URL copied!'));
    };

    const handlePreview = async (t: TemplateListItem) => {
        try {
            const res = await axios.get(`${api}/v1/Form/detail`, { ...auth(), params: { code: t.templateCode } });
            const schema = res.data.schemaJson ? JSON.parse(res.data.schemaJson) : { fields: [] };
            setPreview({
                name: res.data.name,
                description: res.data.description,
                fields: schema.fields ?? [],
                languages: schema.languages,
                names: schema.names,
                descriptions: schema.descriptions,
            });
        } catch { toast.error('Failed to load form'); }
    };

    if (loading) return <LoadingScreen />;

    return (
        <div className="admin-layout">
            <Topbar role={role} photoData={photoData ?? undefined} token={token} />
            <main className="admin-main" style={{ padding: '36px 40px' }}>
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Page header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-2">
                        <BiLayout size={22} className="text-danger" />
                        <h4 className="mb-0 fw-bold">Forms</h4>
                    </div>
                    <Button
                        style={{ background: '#c5281f', border: 'none' }}
                        onClick={() => navigate('/form-builder')}
                    >
                        <BiPlus /> Create Form
                    </Button>
                </div>

                {/* Filters */}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <div className="input-group" style={{ maxWidth: 320 }}>
                        <span className="input-group-text bg-white border-end-0 text-muted" style={{ borderRadius: '8px 0 0 8px' }}>
                            <BiSearch size={16} />
                        </span>
                        <Form.Control
                            placeholder="Search forms…"
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            className="border-start-0"
                            style={{ borderRadius: 0 }}
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={handleSearch}
                            style={{ borderRadius: '0 8px 8px 0', fontSize: 13 }}
                        >
                            Search
                        </Button>
                    </div>
                    <Form.Select
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(0); load(0, keyword, e.target.value); }}
                        style={{ maxWidth: 160 }}
                    >
                        <option value="">All Statuses</option>
                        <option value="Draft">Draft</option>
                        <option value="Published">Published</option>
                        <option value="Closed">Closed</option>
                    </Form.Select>
                </div>

                {/* Table */}
                {tableLoading ? (
                    <div className="text-center py-5"><Spinner /></div>
                ) : (
                    <>
                        <Table hover className="mb-3 bg-white rounded shadow-sm" style={{ overflow: 'hidden' }}>
                            <thead className="table-light">
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Open Date</th>
                                    <th>Close Date</th>
                                    <th>Responses</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.templates ?? []).length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-5">
                                            No forms yet. Click <strong>Create Form</strong> to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    (data?.templates ?? []).map((t: TemplateListItem) => (
                                        <tr key={t.id}>
                                            <td className="text-muted small">{t.templateCode}</td>
                                            <td className="fw-semibold">{t.name}</td>
                                            <td><StatusBadge status={t.status} /></td>
                                            <td className="small">{t.openDate  ? moment.utc(t.openDate).tz('Asia/Bangkok').format('DD-MMM-YYYY')  : '-'}</td>
                                            <td className="small">{t.closeDate ? moment.utc(t.closeDate).tz('Asia/Bangkok').format('DD-MMM-YYYY') : '-'}</td>
                                            <td>{t.responseCount}</td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end align-items-center gap-1">
                                                    {/* Primary: Reports (Published/Closed only) */}
                                                    {(t.status === 'Published' || t.status === 'Closed') && (
                                                        <Button
                                                            size="sm" variant="outline-dark"
                                                            title="View responses"
                                                            onClick={() => navigate(`/form-report?code=${t.templateCode}`)}
                                                        >
                                                            <BiBarChartAlt2 />
                                                        </Button>
                                                    )}
                                                    {/* Primary: Edit */}
                                                    <Button
                                                        size="sm" variant="outline-primary"
                                                        title="Edit"
                                                        onClick={() => navigate(`/form-builder?code=${t.templateCode}`)}
                                                    >
                                                        <BiEdit />
                                                    </Button>
                                                    {/* Primary: Delete */}
                                                    <Button
                                                        size="sm" variant="outline-danger"
                                                        title="Delete"
                                                        onClick={() => handleDelete(t)}
                                                    >
                                                        <BiTrash />
                                                    </Button>
                                                    {/* Secondary: More dropdown */}
                                                    <Dropdown align="end">
                                                        <Dropdown.Toggle
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            id={`more-${t.id}`}
                                                            title="More actions"
                                                            style={{ lineHeight: 1, padding: '4px 7px' }}
                                                        >
                                                            <BiDotsHorizontalRounded size={16} />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => handlePreview(t)}>
                                                                <BiShow className="me-2" size={15} /> Preview
                                                            </Dropdown.Item>
                                                            <Dropdown.Item onClick={() => handleDuplicate(t.templateCode)}>
                                                                <BiCopy className="me-2" size={15} /> Duplicate
                                                            </Dropdown.Item>
                                                            {t.status === 'Published' && <>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item onClick={() => handleCopyUrl(t.templateCode)}>
                                                                    <BiLink className="me-2" size={15} /> Copy Form URL
                                                                </Dropdown.Item>
                                                            </>}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>

                        {(data?.totalPages ?? 0) > 1 && (
                            <ReactPaginate
                                pageCount={data?.totalPages ?? 1}
                                forcePage={page}
                                onPageChange={({ selected }) => { setPage(selected); load(selected); }}
                                previousLabel="‹"
                                nextLabel="›"
                                containerClassName="pagination justify-content-center"
                                pageClassName="page-item"
                                pageLinkClassName="page-link"
                                previousClassName="page-item"
                                previousLinkClassName="page-link"
                                nextClassName="page-item"
                                nextLinkClassName="page-link"
                                activeClassName="active"
                            />
                        )}
                        <div className="text-muted small">Total: {data?.total ?? 0} templates</div>
                    </>
                )}

                {preview && (
                    <FormPreviewModal
                        show
                        onHide={() => setPreview(null)}
                        name={preview.name}
                        description={preview.description}
                        fields={preview.fields}
                        languages={preview.languages}
                        names={preview.names}
                        descriptions={preview.descriptions}
                    />
                )}
            </main>
        </div>
    );
}
